import Image from 'next/image';
import router from 'next/router';
import { useState, useEffect } from 'react';

import { Box, Grid, Chip, Typography, Stack } from '@mui/material';
import { useTranslation } from 'next-i18next';

import { FooterCpdDetail } from './style';

import EnrollButton from '@components/molecules/enrollButton';
import { CourseCpdDetailContainer } from 'src/components/templates/coursesCpdDetail/style';
import { EnrollmentStatus } from 'src/helpers/constants';
import { getLicenseCPDPath, getLicenseTypes, numberWithCommas } from 'src/helpers/utils';
import { ICourse } from 'src/interfaces';

interface Props {
  course: ICourse;
  height: number;
  onChooseTrainingClick: () => void;
  originalPrice: number;
  salePrice: number;
  cpdEnrollmentStatus: EnrollmentStatus;
  canEnrollToCourse?: boolean;
  isLoading?: boolean;
}

const Footer = ({
  course,
  height,
  onChooseTrainingClick,
  originalPrice,
  salePrice,
  cpdEnrollmentStatus,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const availableDaysToLearn = course.default_glances?.available_days_to_learn;

  return (
    <FooterCpdDetail isSticky={scrollY - 50 > height}>
      <CourseCpdDetailContainer padding="1rem">
        <Box>
          <Grid container sx={{ height: '100%', display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <Grid xs={4} item>
              {!originalPrice || originalPrice === salePrice || originalPrice < salePrice ? (
                <Typography component="div" variant="h6_deprecated">
                  {salePrice
                    ? `${numberWithCommas(salePrice)} ${t('home.course_card_baht')}`
                    : t('home.course_card_free')}
                </Typography>
              ) : (
                <>
                  <Typography component="div" variant="h3" lineHeight="0.7" sx={{ mt: 1 }} color="white">
                    {salePrice
                      ? `${numberWithCommas(salePrice)} ${t('home.course_card_baht')}`
                      : t('home.course_card_free')}
                  </Typography>
                  <Typography component="div" className="strike-center text-discount" variant="h6_deprecated">
                    {numberWithCommas(originalPrice)} {t('home.course_card_baht')}
                  </Typography>
                </>
              )}
            </Grid>
            <Grid xs={8} item>
              <EnrollButton
                type={cpdEnrollmentStatus}
                availableDaysToLearn={availableDaysToLearn}
                onClick={onChooseTrainingClick}
                isLoading={isLoading}
                isFooter
                isCpd
              />
            </Grid>
          </Grid>
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Box
              sx={{
                display: { lg: 'block', xs: 'none' },
                position: 'relative',
                width: '186px',
                img: { borderRadius: '10px' },
              }}
            >
              <Image src={course.highlight_image} alt={course.name} layout="fill" />
            </Box>
            <Stack direction="row" justifyContent="space-between" sx={{ width: { lg: '85%', xs: '100%' } }}>
              <Box sx={{ ml: { lg: '36px', sm: 0 }, width: { md: '90%', lg: '60%' } }}>
                {course.licenseTypes?.map((license, index) => (
                  <Chip
                    key={index}
                    onClick={() => router.push(getLicenseCPDPath(license))}
                    label={getLicenseTypes(license)}
                    variant="outlined"
                    sx={{
                      mr: 1,
                      color: 'white',
                      mb: '4px',
                      typography: 'h7',
                      borderColor: 'white',
                    }}
                  />
                ))}
                <Typography
                  sx={{ typography: { md: 'h4', xs: 'h7' }, lineHeight: 1 }}
                  color="white"
                  className="ellipsis-2"
                >
                  {course.name}
                </Typography>
              </Box>
              <Box sx={{ width: { lg: '35%', md: '70%' } }}>
                {!originalPrice || originalPrice === salePrice || originalPrice < salePrice ? (
                  <Typography
                    component="span"
                    sx={{ mb: 2, typography: { md: 'h1', xs: 'h3' }, lineHeight: 1 }}
                    color="white"
                  >
                    {salePrice
                      ? `${numberWithCommas(salePrice)} ${t('home.course_card_baht')}`
                      : t('home.course_card_free')}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      component="span"
                      sx={{ mb: 2, typography: { md: 'h1', xs: 'h3' }, lineHeight: 1 }}
                      color="white"
                    >
                      {salePrice
                        ? `${numberWithCommas(salePrice)} ${t('home.course_card_baht')}`
                        : t('home.course_card_free')}
                    </Typography>
                    <Typography
                      className="strike-center text-discount"
                      sx={{
                        mx: '1rem',
                        typography: { md: 'h4', xs: 'h7' },
                        position: 'relative',
                        bottom: 2,
                      }}
                      component="span"
                    >
                      {numberWithCommas(originalPrice)} {t('home.course_card_baht')}
                    </Typography>
                  </>
                )}
                <EnrollButton
                  type={cpdEnrollmentStatus}
                  availableDaysToLearn={availableDaysToLearn}
                  onClick={onChooseTrainingClick}
                  isLoading={isLoading}
                  isFooter
                  isCpd
                />
              </Box>
            </Stack>
          </Box>
        </Box>
      </CourseCpdDetailContainer>
    </FooterCpdDetail>
  );
};

export default Footer;
